import React, { useContext } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DownloadContext } from './context/DownloadContext';
import { CONTACT_SUPPORT } from '../../constants/urls';
import ImgDeviceChromebook from '../../images/device-type-chromebook.svg';
import ImgDeviceMac from '../../images/device-type-macos.svg';
import ImgDeviceTablet from '../../images/device-type-tablet.svg';
import ImgDeviceWindows from '../../images/device-type-windows.svg';
import ImgBgGridPurple from '../../images/grid-background-class-purple.png';
import ImgHeroTeams from '../../images/hero-class-for-teams.png';
import ImgHero from '../../images/hero-download-classroom-view-avatars.png';

const Container = styled('div')`
    height: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
`;

const MainCol = styled('div')`
    display: flex;
    align-items: center;
    text-align: center;
`;

const MainCol1 = styled(MainCol)`
    display: flex;
    align-items: center;
    text-align: center;
`;

const MainCol2 = styled(MainCol)`
    background-image: url(${ImgBgGridPurple});
    background-repeat: no-repeat;
    background-size: cover;
    color: #fff;
`;

const MainColBody = styled('div')`
    width: calc(100% - 96px);
    margin: 1% auto;
`;

const HeroTitle = styled('h2')`
    font-style: normal;
    font-weight: 400;
    font-size: 30px;
    line-height: 35px;
`;

const HeroImg = styled('img')`
    width: 100%;
`;

const DownloadOptions = styled('div')`
    display: inline-block;
    width: 375px;
    max-width: 110%;
    padding: 24px;
    background-color: #ffffff;
    box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px rgb(0 0 0 / 14%), 0px 1px 10px rgb(0 0 0 / 12%);
    border-radius: 8px;
    text-align: center;
    margin: 20px 0 16px;
    h2 {
        line-height: 35.16px;
        margin: 20px 0 16px;
        font-weight: 500;
        font-size: 30px;
    }
`;

const LaunchWebApp = styled('div')`
    display: inline-block;
    width: 375px;
    max-width: 110%;
    padding: 24px;
    background-color: #ffffff;
    box-shadow: 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px rgb(0 0 0 / 14%), 0px 1px 10px rgb(0 0 0 / 12%);
    border-radius: 8px;
    text-align: center;
    margin: 20px 0 16px;
    h2 {
        line-height: 35.16px;
        margin: 20px 0 16px;
        font-weight: 500;
        font-size: 30px;
    }
`;

const AlreadyJoinedText = styled('div')`
    margin: 24px 0;
    font-size: 18px;
`;

const LogoImage = styled('img')`
    display: block;
    max-height: 20vh;
    max-width: calc(80% - 3rem);
    margin: 1.5rem auto;
`;

const DeviceWarning = styled('div')`
    text-align: left;
    margin: 0.5rem 0 1rem;
    padding: 8px 16px;
    background-color: ${(props) => props.theme.colors.primary.light};
    h3 {
        margin-top: 0;
        font-weight: 500;
        font-size: 16px;
    }
    p {
        margin-bottom: 0;
    }
`;

const SupportedDevices = styled('div')`
    margin: 0 0 1rem;
    border: 1px solid ${(props) => props.theme.colors.primary.light};
    h3 {
        font-weight: 400;
        font-size: 16px;
    }
`;

const SupportedDevicesGrid = styled('div')`
    display: grid;
    grid-template-columns: 1fr 1fr;
    > div {
        padding: 1rem;
        text-align: center;
    }
`;

const StyledAccordion = styled(Accordion)`
    text-align: left;
    margin: 0;
`;

const Body: React.FC = () => {
    const { schoolInfo, userAgent, adminSettings, downloadURL, isTeamsSchool, isHipaa } = useContext(DownloadContext);
    const { t } = useTranslation('class');

    const hasApp = (isTeamsSchool ? false : adminSettings?.has_app) || false;
    const hasWebApp = isTeamsSchool || adminSettings?.has_web_app || false;

    // There's no way that our language strings are going to result in XSS, but I'm purifying them anyway.
    const tHTML = (key: string) => ({ __html: DOMPurify.sanitize(t(key)) });
    return (
        <Container>
            <MainCol1>
                <MainColBody className="main-column-body">
                    {schoolInfo?.logo && (
                        <LogoImage src={schoolInfo?.logo} id="logo-image" alt="" role="presentation" />
                    )}
                    {hasApp && !isHipaa ? (
                        <>
                            {!userAgent.deviceSupported ? (
                                <>
                                    <DeviceWarning>
                                        <h3>
                                            {userAgent.deviceType === 'mobile'
                                                ? t('device-support-warning-header-mobile')
                                                : t('device-support-warning-header')}
                                        </h3>
                                        <p>{t('device-support-warning')}</p>
                                    </DeviceWarning>
                                    <SupportedDevices>
                                        <h3>{t('device-support-header')}</h3>
                                        <SupportedDevicesGrid>
                                            <div>
                                                <img src={ImgDeviceMac} alt="" role="presentation" />
                                                <Typography align="center">{t('device-type-macos')}</Typography>
                                            </div>
                                            <div>
                                                <img src={ImgDeviceWindows} alt="" role="presentation" />
                                                <Typography align="center">{t('device-type-windows')}</Typography>
                                            </div>
                                            <div>
                                                <img src={ImgDeviceTablet} alt="" role="presentation" />
                                                <Typography align="center">{t('device-type-tablet')}</Typography>
                                            </div>
                                            <div>
                                                <img src={ImgDeviceChromebook} alt="" role="presentation" />
                                                <Typography align="center">{t('device-type-chromebook')}</Typography>
                                            </div>
                                        </SupportedDevicesGrid>
                                    </SupportedDevices>
                                    <Box sx={{ marginBottom: '1rem' }}>
                                        <StyledAccordion>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="os-support-detail"
                                                id="os-support-header"
                                            >
                                                <Typography>{t('os-support-header')}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails id="os-support-detail">
                                                <div>
                                                    <ul>
                                                        {t('os-support')
                                                            .split('|')
                                                            .map((r, idx) => (
                                                                <li key={idx}>{r.trim()}</li>
                                                            ))}
                                                    </ul>
                                                </div>
                                                <Typography>
                                                    <a href={CONTACT_SUPPORT}>{t('os-support-link')}</a>
                                                </Typography>
                                            </AccordionDetails>
                                        </StyledAccordion>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <DownloadOptions>
                                        <h2 dangerouslySetInnerHTML={tHTML('download-class-2')}></h2>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                window.location.href = downloadURL;
                                            }}
                                        >
                                            {userAgent.isMac ? t('download-macos-11') : t('download')}
                                        </Button>
                                    </DownloadOptions>

                                    <div id="download_alt" className="download-alt"></div>

                                    <AlreadyJoinedText>{t('class-installed')}</AlreadyJoinedText>
                                </>
                            )}
                        </>
                    ) : (
                        <> </>
                    )}
                    {hasWebApp ? (
                        <>
                            <LaunchWebApp>
                                <h2 dangerouslySetInnerHTML={tHTML('launch-class-web')}></h2>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        window.location.href = '/v1_index_client.html?websdk=true';
                                    }}
                                >
                                    {t('launch')}
                                </Button>
                            </LaunchWebApp>
                        </>
                    ) : (
                        <> </>
                    )}
                </MainColBody>
            </MainCol1>
            <MainCol2>
                <MainColBody>
                    <HeroTitle className="hero-title">{t(isTeamsSchool ? 'hero-title-teams' : 'hero-title')}</HeroTitle>
                    <HeroImg src={isTeamsSchool ? ImgHeroTeams : ImgHero} alt="" role="presentation" />
                </MainColBody>
            </MainCol2>
        </Container>
    );
};

export default Body;
